/**
 * Biquad design and curve fitting.
 *
 * Used wherever a smooth, low-order magnitude correction is cheaper and lower
 * latency than an FIR: analog output-stage response, amplifier bandwidth,
 * amplifier/loudspeaker load interaction, cabinet resonances, user EQ.
 */

import type { CompiledBiquad } from '../types/compiled';
import type { FRCurve } from '../types/curves';
import { evalFR } from '../types/curves';

const TWO_PI = Math.PI * 2;

export function peaking(fs: number, f0: number, gainDb: number, q: number): CompiledBiquad {
  const A = Math.pow(10, gainDb / 40);
  const w0 = (TWO_PI * Math.min(f0, fs * 0.49)) / fs;
  const alpha = Math.sin(w0) / (2 * Math.max(0.05, q));
  const cw = Math.cos(w0);
  const a0 = 1 + alpha / A;
  return {
    b0: (1 + alpha * A) / a0,
    b1: (-2 * cw) / a0,
    b2: (1 - alpha * A) / a0,
    a1: (-2 * cw) / a0,
    a2: (1 - alpha / A) / a0,
  };
}

export function lowShelf(fs: number, f0: number, gainDb: number, s = 0.9): CompiledBiquad {
  const A = Math.pow(10, gainDb / 40);
  const w0 = (TWO_PI * Math.min(f0, fs * 0.49)) / fs;
  const cw = Math.cos(w0);
  const sw = Math.sin(w0);
  const alpha = (sw / 2) * Math.sqrt((A + 1 / A) * (1 / s - 1) + 2);
  const tsa = 2 * Math.sqrt(A) * alpha;
  const a0 = A + 1 + (A - 1) * cw + tsa;
  return {
    b0: (A * (A + 1 - (A - 1) * cw + tsa)) / a0,
    b1: (2 * A * (A - 1 - (A + 1) * cw)) / a0,
    b2: (A * (A + 1 - (A - 1) * cw - tsa)) / a0,
    a1: (-2 * (A - 1 + (A + 1) * cw)) / a0,
    a2: (A + 1 + (A - 1) * cw - tsa) / a0,
  };
}

export function highShelf(fs: number, f0: number, gainDb: number, s = 0.9): CompiledBiquad {
  const A = Math.pow(10, gainDb / 40);
  const w0 = (TWO_PI * Math.min(f0, fs * 0.49)) / fs;
  const cw = Math.cos(w0);
  const sw = Math.sin(w0);
  const alpha = (sw / 2) * Math.sqrt((A + 1 / A) * (1 / s - 1) + 2);
  const tsa = 2 * Math.sqrt(A) * alpha;
  const a0 = A + 1 - (A - 1) * cw + tsa;
  return {
    b0: (A * (A + 1 + (A - 1) * cw + tsa)) / a0,
    b1: (-2 * A * (A - 1 + (A + 1) * cw)) / a0,
    b2: (A * (A + 1 + (A - 1) * cw - tsa)) / a0,
    a1: (2 * (A - 1 - (A + 1) * cw)) / a0,
    a2: (A + 1 - (A - 1) * cw - tsa) / a0,
  };
}

export function highPass(fs: number, f0: number, q = Math.SQRT1_2): CompiledBiquad {
  const w0 = (TWO_PI * Math.min(f0, fs * 0.49)) / fs;
  const cw = Math.cos(w0);
  const alpha = Math.sin(w0) / (2 * Math.max(0.05, q));
  const a0 = 1 + alpha;
  return {
    b0: ((1 + cw) / 2) / a0,
    b1: (-(1 + cw)) / a0,
    b2: ((1 + cw) / 2) / a0,
    a1: (-2 * cw) / a0,
    a2: (1 - alpha) / a0,
  };
}

export function lowPass(fs: number, f0: number, q = Math.SQRT1_2): CompiledBiquad {
  const w0 = (TWO_PI * Math.min(f0, fs * 0.49)) / fs;
  const cw = Math.cos(w0);
  const alpha = Math.sin(w0) / (2 * Math.max(0.05, q));
  const a0 = 1 + alpha;
  return {
    b0: ((1 - cw) / 2) / a0,
    b1: (1 - cw) / a0,
    b2: ((1 - cw) / 2) / a0,
    a1: (-2 * cw) / a0,
    a2: (1 - alpha) / a0,
  };
}

export function notch(fs: number, f0: number, q = 4): CompiledBiquad {
  const w0 = (TWO_PI * Math.min(f0, fs * 0.49)) / fs;
  const cw = Math.cos(w0);
  const alpha = Math.sin(w0) / (2 * Math.max(0.05, q));
  const a0 = 1 + alpha;
  return { b0: 1 / a0, b1: (-2 * cw) / a0, b2: 1 / a0, a1: (-2 * cw) / a0, a2: (1 - alpha) / a0 };
}

export const UNITY: CompiledBiquad = { b0: 1, b1: 0, b2: 0, a1: 0, a2: 0 };

/** Magnitude response of one biquad at frequency f. */
export function biquadMagDb(b: CompiledBiquad, fs: number, f: number): number {
  const w = (TWO_PI * f) / fs;
  const cw = Math.cos(w);
  const c2w = Math.cos(2 * w);
  const sw = Math.sin(w);
  const s2w = Math.sin(2 * w);
  const nr = b.b0 + b.b1 * cw + b.b2 * c2w;
  const ni = -(b.b1 * sw + b.b2 * s2w);
  const dr = 1 + b.a1 * cw + b.a2 * c2w;
  const di = -(b.a1 * sw + b.a2 * s2w);
  const num = Math.hypot(nr, ni);
  const den = Math.hypot(dr, di) || 1e-12;
  return 20 * Math.log10(Math.max(1e-9, num / den));
}

export function cascadeMagDb(bs: CompiledBiquad[], fs: number, f: number): number {
  let s = 0;
  for (const b of bs) s += biquadMagDb(b, fs, f);
  return s;
}

export function biquadPhaseDeg(b: CompiledBiquad, fs: number, f: number): number {
  const w = (TWO_PI * f) / fs;
  const cw = Math.cos(w);
  const c2w = Math.cos(2 * w);
  const sw = Math.sin(w);
  const s2w = Math.sin(2 * w);
  const nr = b.b0 + b.b1 * cw + b.b2 * c2w;
  const ni = -(b.b1 * sw + b.b2 * s2w);
  const dr = 1 + b.a1 * cw + b.a2 * c2w;
  const di = -(b.a1 * sw + b.a2 * s2w);
  return ((Math.atan2(ni, nr) - Math.atan2(di, dr)) * 180) / Math.PI;
}

/**
 * Fit a cascade of peaking/shelving biquads to an arbitrary magnitude curve.
 *
 * Greedy residual minimisation on a log-frequency grid: repeatedly find the
 * frequency of largest remaining error and place a peaking filter there, with
 * the Q chosen from the local width of the error. Converges fast and is stable,
 * which matters because this runs whenever the user swaps hardware.
 */
export function fitCurve(
  curve: FRCurve | undefined,
  fs: number,
  maxSections = 8,
  opts: { fMin?: number; fMax?: number; toleranceDb?: number; shelves?: boolean } = {},
): CompiledBiquad[] {
  if (!curve || curve.points.length < 2) return [];
  const fMin = opts.fMin ?? 15;
  const fMax = Math.min(opts.fMax ?? 22000, fs * 0.48);
  const tol = opts.toleranceDb ?? 0.12;
  const N = 220;

  const freqs = new Float64Array(N);
  const target = new Float64Array(N);
  for (let i = 0; i < N; i++) {
    const f = fMin * Math.pow(fMax / fMin, i / (N - 1));
    freqs[i] = f;
    target[i] = evalFR(curve, f);
  }

  const out: CompiledBiquad[] = [];
  const current = new Float64Array(N);

  // Optional shelves first: they model broad tilts far more economically than
  // peaking sections do.
  if (opts.shelves !== false) {
    const lowErr = target[0] - target[Math.floor(N * 0.5)];
    if (Math.abs(lowErr) > 0.6) {
      const b = lowShelf(fs, 110, lowErr, 0.7);
      out.push(b);
      for (let i = 0; i < N; i++) current[i] += biquadMagDb(b, fs, freqs[i]);
    }
    const hiErr = target[N - 1] - target[Math.floor(N * 0.5)];
    if (Math.abs(hiErr) > 0.6) {
      const b = highShelf(fs, 7000, hiErr, 0.7);
      out.push(b);
      for (let i = 0; i < N; i++) current[i] += biquadMagDb(b, fs, freqs[i]);
    }
  }

  for (let s = out.length; s < maxSections; s++) {
    let worst = 0;
    let wi = -1;
    for (let i = 0; i < N; i++) {
      const e = Math.abs(target[i] - current[i]);
      if (e > worst) { worst = e; wi = i; }
    }
    if (wi < 0 || worst < tol) break;

    const err = target[wi] - current[wi];
    // Estimate the width of this error feature: walk out until the error halves.
    let lo = wi;
    let hi = wi;
    const halfLevel = Math.abs(err) * 0.5;
    while (lo > 0 && Math.abs(target[lo] - current[lo]) > halfLevel) lo--;
    while (hi < N - 1 && Math.abs(target[hi] - current[hi]) > halfLevel) hi++;
    const bwOct = Math.log2(Math.max(1.02, freqs[hi] / freqs[lo]));
    const q = Math.min(12, Math.max(0.35, 1 / (2 * Math.sinh((Math.LN2 / 2) * bwOct))));

    const b = peaking(fs, freqs[wi], err, q);
    out.push(b);
    for (let i = 0; i < N; i++) current[i] += biquadMagDb(b, fs, freqs[i]);
  }

  return out;
}

/** Residual error of a fit, for the inspector's honesty readout. */
export function fitError(curve: FRCurve | undefined, bs: CompiledBiquad[], fs: number): number {
  if (!curve) return 0;
  let worst = 0;
  for (let i = 0; i < 200; i++) {
    const f = 20 * Math.pow(20000 / 20, i / 199);
    worst = Math.max(worst, Math.abs(evalFR(curve, f) - cascadeMagDb(bs, fs, f)));
  }
  return worst;
}

/** Serialise a cascade into a flat Float32Array for transfer to a worklet. */
export function flattenBiquads(bs: CompiledBiquad[]): Float32Array {
  const out = new Float32Array(bs.length * 5);
  for (let i = 0; i < bs.length; i++) {
    out[i * 5 + 0] = bs[i].b0;
    out[i * 5 + 1] = bs[i].b1;
    out[i * 5 + 2] = bs[i].b2;
    out[i * 5 + 3] = bs[i].a1;
    out[i * 5 + 4] = bs[i].a2;
  }
  return out;
}
